'use client';

import { useEffect, useState } from 'react';

// ── Types matching the neighbourhood API response ───────────────────────────

interface SafetySummary {
  crime_rating: string;
  crimes_per_thousand: number;
  population: number;
  top_crimes: Array<{ type: string; count: number }>;
  observations: string[];
}

interface SchoolSummary {
  name: string;
  type: string;
  phase: string;
  distance: string;
  num_pupils: number;
  postcode: string;
}

interface DemographicsSummary {
  deprivation_index: number;
  health_index: number;
  proportion_with_degree: number;
  vehicles_per_household: string;
  social_grade: Record<string, string>;
  age_distribution: Record<string, string>;
  commute_methods: Record<string, string>;
}

interface MarketDemandSummary {
  demand_rating: string;
  days_on_market: number;
  months_of_inventory: string;
  total_for_sale: number;
  average_sales_per_month: number;
  turnover_per_month: string;
}

interface PlanningSummary {
  total_applications: number;
  approved: number;
  pending: number;
  refused: number;
  recent: Array<{
    address: string;
    proposal: string;
    status: string;
    decision: string;
  }>;
}

interface InvestmentScoreBreakdown {
  capital_growth: { score: number; weight: number; detail: string };
  rental_yield: { score: number; weight: number; detail: string };
  market_demand: { score: number; weight: number; detail: string };
  gentrification: { score: number; weight: number; detail: string };
  risk: { score: number; weight: number; detail: string };
}

interface NeighbourhoodData {
  postcode: string;
  fetched_at: string;
  safety: SafetySummary | null;
  schools: { state: SchoolSummary[]; independent: SchoolSummary[] } | null;
  demographics: DemographicsSummary | null;
  household_income: { average: number; currency: string } | null;
  flood_risk: string | null;
  transport: { ptal: string } | null;
  market_demand: MarketDemandSummary | null;
  planning: PlanningSummary | null;
  data_completeness: number;
  investment?: InvestmentData;
}

interface InvestmentData {
  overall_score: number;
  rating: string;
  breakdown: InvestmentScoreBreakdown;
  summary: string;
}

interface OfflineAnalytics {
  annual_growth_pct: number;
  r2: number;
  volatility: number;
  gentrification_stage: string | null;
  gentrification_index: number | null;
}

interface Props {
  postcode: string;
  offlineAnalytics?: OfflineAnalytics;
}

// ── Client-side offline investment score ────────────────────────────────────

function computeOfflineInvestment(a: OfflineAnalytics): InvestmentData {
  const norm = (v: number, min: number, max: number) =>
    Math.max(0, Math.min(100, ((v - min) / (max - min)) * 100));

  let cgScore = norm(a.annual_growth_pct, -2, 10);
  if (a.r2 < 0.7) cgScore *= 0.7;
  cgScore = Math.round(cgScore);

  const stageScores: Record<string, number> = {
    'early-signs': 70, 'emerging': 90, 'mid-wave': 65, 'established': 40,
  };
  const gentScore = a.gentrification_stage ? (stageScores[a.gentrification_stage] ?? 50) : 50;

  let riskPenalty = 0;
  if (a.volatility > 0.15) riskPenalty += 15;
  else if (a.volatility > 0.10) riskPenalty += 5;
  const riskScore = Math.max(0, 100 - riskPenalty);

  const breakdown: InvestmentScoreBreakdown = {
    capital_growth: { score: cgScore, weight: 0.30, detail: `${a.annual_growth_pct >= 0 ? '+' : ''}${a.annual_growth_pct.toFixed(1)}% annual growth` },
    rental_yield: { score: 50, weight: 0.25, detail: 'Loading live yield data\u2026' },
    market_demand: { score: 50, weight: 0.20, detail: 'Loading live demand data\u2026' },
    gentrification: { score: gentScore, weight: 0.15, detail: `${a.gentrification_stage ?? 'unknown'} stage` },
    risk: { score: riskScore, weight: 0.10, detail: a.volatility > 0.15 ? 'High price volatility' : 'Moderate risk' },
  };

  const overall = Math.round(
    Object.values(breakdown).reduce((s, b) => s + b.score * b.weight, 0),
  );

  let rating: string;
  if (overall >= 80) rating = 'Excellent';
  else if (overall >= 65) rating = 'Good';
  else if (overall >= 45) rating = 'Fair';
  else rating = 'Poor';

  return { overall_score: overall, rating, breakdown, summary: `Preliminary score based on offline analytics. Live data loading\u2026` };
}

// ── Colour helpers ──────────────────────────────────────────────────────────

function crimeColor(rating: string): string {
  const r = rating.toLowerCase();
  if (r === 'very low' || r === 'low') return 'text-emerald-600 bg-emerald-50';
  if (r === 'average') return 'text-amber-600 bg-amber-50';
  return 'text-red-600 bg-red-50';
}

function demandColor(rating: string): string {
  const r = rating.toLowerCase();
  if (r.includes('strong')) return 'text-emerald-600 bg-emerald-50';
  if (r === 'balanced') return 'text-amber-600 bg-amber-50';
  return 'text-red-600 bg-red-50';
}

function floodColor(risk: string): string {
  const r = risk.toLowerCase();
  if (r === 'very low' || r === 'low') return 'text-emerald-600 bg-emerald-50';
  if (r === 'medium') return 'text-amber-600 bg-amber-50';
  return 'text-red-600 bg-red-50';
}

function scoreColor(score: number): string {
  if (score >= 80) return 'text-emerald-700';
  if (score >= 65) return 'text-emerald-600';
  if (score >= 45) return 'text-amber-600';
  return 'text-red-600';
}

function scoreBg(score: number): string {
  if (score >= 80) return 'bg-emerald-500';
  if (score >= 65) return 'bg-emerald-400';
  if (score >= 45) return 'bg-amber-400';
  return 'bg-red-400';
}

function formatGBP(v: number): string {
  if (v >= 1_000_000) return `\u00A3${(v / 1_000_000).toFixed(1)}m`;
  if (v >= 1_000) return `\u00A3${Math.round(v / 1_000).toLocaleString()}k`;
  return `\u00A3${v.toLocaleString()}`;
}

// ── Sub-components ──────────────────────────────────────────────────────────

function SectionCard({
  title,
  children,
  icon,
}: {
  title: string;
  children: React.ReactNode;
  icon: string;
}) {
  return (
    <div className="rounded-xl border border-black/10 bg-white p-4 shadow-sm">
      <h5 className="mb-3 flex items-center gap-2 text-sm font-semibold text-[#3a3935]">
        <span className="text-base">{icon}</span>
        {title}
      </h5>
      {children}
    </div>
  );
}

function Badge({
  label,
  colorClass,
}: {
  label: string;
  colorClass: string;
}) {
  return (
    <span
      className={`inline-block rounded-md px-2 py-0.5 text-[11px] font-semibold ${colorClass}`}
    >
      {label}
    </span>
  );
}

function ScoreBar({
  label,
  score,
  detail,
}: {
  label: string;
  score: number;
  detail: string;
}) {
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium text-[#3a3935]">{label}</span>
        <span className={`text-xs font-bold ${scoreColor(score)}`}>{score}/100</span>
      </div>
      <div className="h-1.5 rounded-full bg-[#f3f0ea]">
        <div
          className={`h-1.5 rounded-full ${scoreBg(score)} transition-all`}
          style={{ width: `${Math.min(100, score)}%` }}
        />
      </div>
      <p className="text-[10px] text-[#7a7870]">{detail}</p>
    </div>
  );
}

// ── Skeleton ────────────────────────────────────────────────────────────────

function Skeleton({ className = '' }: { className?: string }) {
  return <div className={`animate-pulse rounded-lg bg-[#f3f0ea] ${className}`} />;
}

function LoadingSkeleton() {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="rounded-xl border border-black/10 p-4">
            <Skeleton className="mb-3 h-4 w-24" />
            <Skeleton className="h-16 w-full" />
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Main component ──────────────────────────────────────────────────────────

export default function NeighbourhoodPanel({ postcode, offlineAnalytics }: Props) {
  const [data, setData] = useState<NeighbourhoodData | null>(null);
  const [investment, setInvestment] = useState<InvestmentData | null>(
    // Compute instant offline score if analytics data available
    offlineAnalytics ? computeOfflineInvestment(offlineAnalytics) : null,
  );
  const [liveLoading, setLiveLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLiveLoading(true);
    setError(null);
    setData(null);

    // Show offline score immediately if available
    if (offlineAnalytics) {
      setInvestment(computeOfflineInvestment(offlineAnalytics));
    }

    const encodedPostcode = encodeURIComponent(postcode);

    // Fetch full neighbourhood data (uses file cache if available — instant)
    fetch(`/api/neighbourhood/${encodedPostcode}`)
      .then((r) => {
        if (!r.ok) throw new Error(`${r.status}`);
        return r.json();
      })
      .then((result: NeighbourhoodData) => {
        setData(result);
        // Replace offline score with live-enriched score
        if (result.investment) setInvestment(result.investment);
      })
      .catch(() => {
        setError('Unable to fetch neighbourhood data');
      })
      .finally(() => setLiveLoading(false));
  }, [postcode, offlineAnalytics]);

  // Show investment score immediately (no loading skeleton if we have offline data)
  if (liveLoading && !investment) return <LoadingSkeleton />;

  if (error && !data && !investment) {
    return (
      <div className="rounded-xl border border-black/10 bg-[#faf8f4] p-6 text-center">
        <p className="text-sm font-medium text-[#7a7870]">
          Neighbourhood data unavailable
        </p>
        <p className="mt-1 text-xs text-[#7a7870]">
          Set PROPERTYDATA_KEY in .env.local to enable live neighbourhood intelligence
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Data completeness / loading indicator */}
      {data ? (
        <div className="flex items-center justify-between">
          <p className="text-xs text-[#7a7870]">
            Live data from PropertyData.co.uk
          </p>
          <span className="text-[10px] font-medium text-[#7a7870]">
            {data.data_completeness}% data coverage
          </span>
        </div>
      ) : liveLoading ? (
        <div className="flex items-center gap-2">
          <div className="h-3 w-3 animate-spin rounded-full border-2 border-[#d4ede5] border-t-[#2d7a5f]" />
          <p className="text-xs text-[#7a7870]">
            Loading live neighbourhood data&hellip;
          </p>
        </div>
      ) : null}

      {/* Investment Score (hero card) */}
      {investment && (
        <div className="rounded-xl border border-[#d4ede5] bg-gradient-to-br from-[#d4ede5]/50 to-white p-5">
          <div className="mb-4 flex items-center justify-between">
            <h4 className="text-sm font-semibold text-[#3a3935]">
              Investment Score
            </h4>
            <div className="text-right">
              <span className={`text-3xl font-black ${scoreColor(investment.overall_score)}`}>
                {investment.overall_score}
              </span>
              <span className="text-sm text-[#7a7870]">/100</span>
              <p className={`text-xs font-semibold ${scoreColor(investment.overall_score)}`}>
                {investment.rating}
              </p>
            </div>
          </div>
          <div className="space-y-3">
            {Object.entries(investment.breakdown).map(([key, val]) => (
              <ScoreBar
                key={key}
                label={key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
                score={val.score}
                detail={val.detail}
              />
            ))}
          </div>
          <p className="mt-3 text-xs text-[#7a7870]">{investment.summary}</p>
        </div>
      )}

      {/* Grid of neighbourhood cards */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {/* Safety */}
        {data?.safety && (
          <SectionCard title="Safety" icon={"\uD83D\uDEE1\uFE0F"}>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs text-[#7a7870]">Crime rating</span>
                <Badge label={data.safety.crime_rating} colorClass={crimeColor(data.safety.crime_rating)} />
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs text-[#7a7870]">Per 1,000 people</span>
                <span className="text-xs font-semibold text-[#3a3935]">
                  {data.safety.crimes_per_thousand}
                </span>
              </div>
              {data.safety.top_crimes.length > 0 && (
                <div className="mt-2 space-y-1">
                  <p className="text-[10px] font-semibold uppercase tracking-wide text-[#7a7870]">
                    Top crime types
                  </p>
                  {data.safety.top_crimes.slice(0, 3).map((c) => (
                    <div key={c.type} className="flex justify-between text-[11px]">
                      <span className="text-[#7a7870] capitalize">{c.type.replace(/_/g, ' ')}</span>
                      <span className="font-medium text-[#3a3935]">{c.count}</span>
                    </div>
                  ))}
                </div>
              )}
              {data.safety.observations.length > 0 && (
                <p className="mt-1 text-[10px] italic text-[#7a7870]">
                  {data.safety.observations[0]}
                </p>
              )}
            </div>
          </SectionCard>
        )}

        {/* Market Demand */}
        {data?.market_demand && (
          <SectionCard title="Market Demand" icon={"\uD83D\uDCC8"}>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs text-[#7a7870]">Demand</span>
                <Badge
                  label={data.market_demand.demand_rating}
                  colorClass={demandColor(data.market_demand.demand_rating)}
                />
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs text-[#7a7870]">Avg days on market</span>
                <span className="text-xs font-semibold text-[#3a3935]">
                  {data.market_demand.days_on_market}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs text-[#7a7870]">Months of inventory</span>
                <span className="text-xs font-semibold text-[#3a3935]">
                  {data.market_demand.months_of_inventory}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs text-[#7a7870]">Properties for sale</span>
                <span className="text-xs font-semibold text-[#3a3935]">
                  {data.market_demand.total_for_sale}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs text-[#7a7870]">Monthly turnover</span>
                <span className="text-xs font-semibold text-[#3a3935]">
                  {data.market_demand.turnover_per_month}
                </span>
              </div>
            </div>
          </SectionCard>
        )}

        {/* Schools */}
        {data?.schools && (data.schools.state.length > 0 || data.schools.independent.length > 0) && (
          <SectionCard title="Schools Nearby" icon={"\uD83C\uDFEB"}>
            <div className="space-y-2">
              {data.schools.state.slice(0, 4).map((s) => (
                <div key={s.name} className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-[11px] font-medium text-[#3a3935]">{s.name}</p>
                    <p className="text-[10px] text-[#7a7870]">
                      {s.phase} &middot; {s.type}
                    </p>
                  </div>
                  <span className="shrink-0 text-[10px] font-medium text-[#7a7870]">
                    {s.distance}
                  </span>
                </div>
              ))}
              {data.schools.independent.length > 0 && (
                <>
                  <p className="text-[10px] font-semibold uppercase tracking-wide text-[#7a7870] pt-1">
                    Independent
                  </p>
                  {data.schools.independent.slice(0, 2).map((s) => (
                    <div key={s.name} className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-[11px] font-medium text-[#3a3935]">{s.name}</p>
                        <p className="text-[10px] text-[#7a7870]">{s.phase}</p>
                      </div>
                      <span className="shrink-0 text-[10px] font-medium text-[#7a7870]">
                        {s.distance}
                      </span>
                    </div>
                  ))}
                </>
              )}
            </div>
          </SectionCard>
        )}

        {/* Demographics */}
        {data?.demographics && (
          <SectionCard title="Demographics" icon={"\uD83D\uDC65"}>
            <div className="space-y-2">
              {data.household_income && (
                <div className="flex items-center justify-between">
                  <span className="text-xs text-[#7a7870]">Avg household income</span>
                  <span className="text-xs font-semibold text-[#3a3935]">
                    {formatGBP(data.household_income.average)}
                  </span>
                </div>
              )}
              <div className="flex items-center justify-between">
                <span className="text-xs text-[#7a7870]">With degree</span>
                <span className="text-xs font-semibold text-[#3a3935]">
                  {(data.demographics.proportion_with_degree * 100).toFixed(0)}%
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs text-[#7a7870]">Deprivation index</span>
                <span className="text-xs font-semibold text-[#3a3935]">
                  {data.demographics.deprivation_index}
                  <span className="text-[#7a7870] font-normal"> /10</span>
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs text-[#7a7870]">Vehicles/household</span>
                <span className="text-xs font-semibold text-[#3a3935]">
                  {data.demographics.vehicles_per_household}
                </span>
              </div>
              {/* Top commute methods */}
              {data.demographics.commute_methods && (
                <div className="mt-1">
                  <p className="text-[10px] font-semibold uppercase tracking-wide text-[#7a7870]">
                    Top commute
                  </p>
                  {Object.entries(data.demographics.commute_methods)
                    .slice(0, 3)
                    .map(([method, pct]) => (
                      <div key={method} className="flex justify-between text-[11px]">
                        <span className="text-[#7a7870] capitalize">{method.replace(/_/g, ' ')}</span>
                        <span className="font-medium text-[#3a3935]">{pct}</span>
                      </div>
                    ))}
                </div>
              )}
            </div>
          </SectionCard>
        )}

        {/* Flood Risk + Transport */}
        {(data?.flood_risk || data?.transport) && (
          <SectionCard title="Environment & Transport" icon={"\uD83D\uDE8D"}>
            <div className="space-y-2">
              {data.flood_risk && (
                <div className="flex items-center justify-between">
                  <span className="text-xs text-[#7a7870]">Flood risk</span>
                  <Badge label={data.flood_risk} colorClass={floodColor(data.flood_risk)} />
                </div>
              )}
              {data.transport && (
                <div className="flex items-center justify-between">
                  <span className="text-xs text-[#7a7870]">PTAL score</span>
                  <span className="text-xs font-semibold text-[#1a4a3a]">
                    {data.transport.ptal}
                  </span>
                </div>
              )}
              {!data.transport && (
                <p className="text-[10px] text-[#7a7870]">
                  PTAL only available for London postcodes
                </p>
              )}
            </div>
          </SectionCard>
        )}

        {/* Planning Applications */}
        {data?.planning && (
          <SectionCard title="Planning Applications" icon={"\uD83C\uDFD7\uFE0F"}>
            <div className="space-y-2">
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="rounded-lg bg-emerald-50 px-2 py-1.5">
                  <p className="text-sm font-bold text-emerald-700">{data.planning.approved}</p>
                  <p className="text-[9px] text-emerald-600">Approved</p>
                </div>
                <div className="rounded-lg bg-amber-50 px-2 py-1.5">
                  <p className="text-sm font-bold text-amber-700">{data.planning.pending}</p>
                  <p className="text-[9px] text-amber-600">Pending</p>
                </div>
                <div className="rounded-lg bg-red-50 px-2 py-1.5">
                  <p className="text-sm font-bold text-red-700">{data.planning.refused}</p>
                  <p className="text-[9px] text-red-600">Refused</p>
                </div>
              </div>
              {data.planning.recent.length > 0 && (
                <div className="mt-1 space-y-1.5">
                  <p className="text-[10px] font-semibold uppercase tracking-wide text-[#7a7870]">
                    Recent
                  </p>
                  {data.planning.recent.slice(0, 2).map((app, i) => (
                    <div key={i} className="text-[10px]">
                      <p className="font-medium text-[#3a3935] truncate">{app.proposal}</p>
                      <p className="text-[#7a7870]">{app.address} &middot; {app.decision}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </SectionCard>
        )}
      </div>
    </div>
  );
}
